import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const currentCategory = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const categoryComics = useMemo(() => {
    let result = comics.filter((c) => c.category === slug);
    if (sortBy === 'latest') {
      result = [...result].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    } else if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else {
      result = [...result].sort((a, b) => b.rating - a.rating);
    }
    return result;
  }, [slug, sortBy]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}韩漫_${currentCategory.name}韩国漫画_免费${currentCategory.name}漫画在线阅读-韩漫漫画网`}
        description={`韩漫漫画网${currentCategory.name}分类提供${currentCategory.description}。海量免费${currentCategory.name}韩国漫画在线看，${currentCategory.count}部精选作品等你发现。`}
        keywords={`${currentCategory.name}韩漫,${currentCategory.name}韩国漫画,免费${currentCategory.name}漫画,韩漫在线阅读,韩漫漫画网`}
        canonicalUrl={`https://www.hanmancomic.com/category/${slug}`}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${currentCategory.name} - 韩漫漫画网`,
          description: currentCategory.description,
          url: `https://www.hanmancomic.com/category/${slug}`,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmancomic.com' },
            { '@type': 'ListItem', position: 2, name: currentCategory.name },
          ],
        }}
      />

      <Navbar />
      <Breadcrumb items={[{ label: '分类浏览', href: '/category/romance' }, { label: currentCategory.name }]} />

      <main>
        {/* Category Header */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className={`${currentCategory.icon} text-xl`}></i>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-600 mt-1">
                {currentCategory.description} · 共 {currentCategory.count} 部作品
              </p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-6">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-700 border border-background-200/70 hover:border-primary-300'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Sort Controls */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500">排序：</span>
            {[
              { key: 'latest', label: '最新更新' },
              { key: 'popular', label: '人气最高' },
              { key: 'rating', label: '评分最高' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key as typeof sortBy)}
                className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  sortBy === opt.key
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-100 border border-background-200/70'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        {/* Comic Grid */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-14">
          {categoryComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {categoryComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂无漫画作品</p>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}